import { log, supabase } from ".";
import { esCotizable } from "../constants/estadosEdificio";
import { EDIFICIOS_EXCLUIDOS } from "../constants/permisos";
import { esOperativoComercial } from "./getEdificiosOperativos";

// ============================================================================
// ÚNICO camino para leer edificios desde una ruta PÚBLICA (sin sesión) — tanda
// de seguridad A3 paso 2 (docs/decisiones/2026-09-27-seguridad-a3-relevamiento.md).
//
// Lee SIEMPRE la vista `public.edificios_publico` (columnas acotadas, WHERE
// eliminada=false, GRANT a anon+authenticated) — NUNCA la tabla real `edificios`.
// Las pantallas admin/privadas siguen usando getEdificios/getEdificiosOperativos/
// getEdificiosListadoPublico/getEdificiosFull (tabla real, select(*), no se tocan).
//
// Reusa los MISMOS predicados que ya existen (esOperativoComercial, esCotizable +
// EDIFICIOS_EXCLUIDOS) — no duplica lógica (regla 10.1). Cada función de abajo es
// el espejo público de su equivalente admin, mismo filtro/orden, columna de menos.
// ============================================================================

const fetchEdificiosPublico = async ({ pais } = {}) => {
  try {
    const data = await supabase.select("edificios_publico", {
      filters: pais ? { pais } : {},
    });
    // Guard cliente (10.114): la vista ya filtra eliminada=false, esto es belt-and-
    // suspenders coherente con el resto de getEdificios*.js.
    return (data ?? []).filter((edificio) => !edificio.eliminada);
  } catch (error) {
    log.error("getEdificiosPublico", error);
    throw error;
  }
};

// Espejo público de getEdificios({pais}) — mismo orden (estado asc, pantallas desc).
// Consumidores: /mapa, /ficha-edificio/:id (vía FichaEdificioInventario), Mapa2
// (/td y /landing para Chile/Perú).
export const getEdificiosPublico = async ({ pais }) => {
  const data = await fetchEdificiosPublico({ pais });
  return [...data].sort((a, b) => {
    if (a.estado !== b.estado) return a.estado < b.estado ? -1 : 1;
    return (b.pantallas || 0) - (a.pantallas || 0);
  });
};

// Espejo público de getEdificiosOperativos({pais}) — mismo predicado esOperativoComercial.
// Consumidores: /simulador-impacto, las 14 rutas /categorias/*, /listadovetos,
// LandingChile/LandingPeruNew (/landing, /td).
export const getEdificiosOperativosPublico = async ({ pais }) => {
  const data = await fetchEdificiosPublico({ pais });
  return data.filter(esOperativoComercial);
};

// Espejo público de getEdificiosListadoPublico({pais}) — mismo predicado esCotizable
// (OPERATIVO + instalando) + vivo + sin EDIFICIOS_EXCLUIDOS.
// Consumidores: /listadoempresas, /inventario (InventarioAvanzado, compartido con
// /mediakit2 — autenticado, también cubierto por el GRANT de la vista), /portafolio.
export const getEdificiosListadoPublicoVista = async ({ pais }) => {
  const data = await fetchEdificiosPublico({ pais });
  return data.filter((e) => esCotizable(e.estado) && !EDIFICIOS_EXCLUIDOS.includes(e.id));
};

// Espejo público de getEdificiosFull() — sin filtro de país (el dropdown de /galeria
// agrupa las 3 operaciones). Único cambio de comportamiento real vs. la tabla: los
// edificios dados de baja dejan de aparecer (la vista los filtra) — decisión
// documentada en el relevamiento del paso 1 (aceptado, consistente con el resto).
export const getEdificiosFullPublico = async () => fetchEdificiosPublico({});
